import moment from "moment/min/moment-with-locales";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, RadialLinearScale, Title, SubTitle } from "chart.js";
import { Pie, Radar } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatTime, mapItemTypeColor } from "../HelperFunctions";
import ClockHand from "./ClockHand";
import { getRelativePosition } from "chart.js/helpers";
import {
    clearMarks,
    decreasePlaybackSpeed,
    downloadMp3,
    increasePlaybackSpeed,
    setCurrentTime,
    setMark,
    startAudioPlay,
    toggleAudioPlay,
} from "./clocksFunctions";
import ClocksContextMenu from "./ClocksContextMenu";
import { useOnKeyEventPress, useRadioName, useResolvedModules, useResolvedRadios } from "../HelperHooks";
import ClockMark from "./ClockMark";
import LoadingIndicator from "../LoadingIndicator";
import { clocksRatingsColors } from "./ClocksRatingsCard";
import MinuteByMinuteTable from "../minuteByMinute/MinuteByMinuteTable";
import ErrorResponse from "../ErrorResponse";
import CreateBitModal from "./CreateBitModal";
import ListenersModal from "../listeners/ListenersModal";
import { Button, Collapse, Stack } from "react-bootstrap";
import { XLg } from "react-bootstrap-icons";
import SongCatalogueModal from "../songCatalogue/SongCatalogueModal";
import SongHistoryModal from "../songHistory/SongHistoryModal";
import TrendOverTime from "../TrendOverTime";
import ClocksBreaksContentModal from "./ClocksBreaksContentModal";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels, Title, SubTitle, RadialLinearScale);

export default function ClocksChart({
    data,
    dataType,
    ratingsData,
    contentData,
    setContentData,
    error,
    loading,
    secondary,
    currentAudioTime,
    showRatingsLabels,
    audioRef,
    setIsPlaying,
    setPlaybackRate,
    isAudioAvailable,
    addToCollectionAllowed,
    markInSeconds,
    markOutSeconds,
    setMarkInSeconds,
    setMarkOutSeconds,
}) {
    const chartRef = useRef(null);
    const chartContainerRef = useRef(null);

    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

    const [clickedTime, setClickedTime] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);

    const [showAddBitModal, setShowAddBitModal] = useState(false);
    const [newBitItem, setNewBitItem] = useState({ placeholder: null, bitStart: null, bitEnd: null });

    const [showMinuteByMinute, setShowMinuteByMinute] = useState(false);
    const [showTrendOvertTime, setShowTrendOvertTime] = useState(false);

    const minuteByMinuteRef = useRef(null);

    const [showListeners, setShowListeners] = useState(false);
    const [showSongHistory, setShowSongHistory] = useState(false);
    const [showSongCatalogue, setShowSongCatalogue] = useState(false);
    const [showBreaksContent, setShowBreaksContent] = useState(false);

    const radioName = useRadioName(data.radio_id);
    const resolvedRadios = useResolvedRadios();
    const resolvedModules = useResolvedModules();

    const contentTestingEnabled = useMemo(() => resolvedModules?.includes(3), [resolvedModules]);
    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);
    const musicTestingEnabled = useMemo(() => resolvedModules?.includes(7), [resolvedModules]);

    // Enable listeners data for PPM only
    const listenersEnabled = useMemo(() => Boolean(resolvedRadios?.find(x => x.radio_id === data.radio_id)?.ppm), [data.radio_id, resolvedRadios]);

    // Focus on clocks container when component mounts
    useEffect(() => {
        setSelectedItem(null);
        if (chartContainerRef.current) {
            chartContainerRef.current.focus();
        }
    }, [data]);

    const onClocksClick = useCallback(
        (event, elements, chart) => {
            const position = getRelativePosition(event, chart);

            // Calc some stuff
            const centerY = Math.ceil(chart.height / 2 + 30);
            const centerX = Math.ceil(chart.width / 2 + 2);
            const dx = position.x - centerX;
            const dy = position.y - centerY;
            const radius = Math.min(chart.width, chart.height - 150) / 2;
            const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

            // If the click is outside the radius of the pie chart, return
            if (distanceFromCenter > radius) return;

            // Convert click position to seconds
            const angleRad = Math.atan2(centerY - position.y, centerX - position.x);
            const anglePerSecond = (2 * Math.PI) / 3600;
            const seconds = (Math.round((angleRad - Math.PI / 2) / anglePerSecond) + 3600) % 3600;
            setClickedTime(Math.round(seconds));

            // Set clicked element if any
            if (elements[0]) setSelectedItem(data.broadcast_entries[elements[0].index]);

            // for right click, we show dropdown menu at clicked position
            if (event.type === "contextmenu") {
                setContextMenuPosition({ x: event.x, y: event.y });
                setShowContextMenu(true);
            } else if (isAudioAvailable) {
                setCurrentTime(audioRef.current, seconds + data.audio_delay);
                startAudioPlay(audioRef.current, setIsPlaying);
            }
        },
        [audioRef, data.audio_delay, data.broadcast_entries, isAudioAvailable, setIsPlaying],
    );

    const chartOptions = useMemo(() => {
        return {
            maintainAspectRatio: false,
            responsive: true,
            layout: {
                padding: {
                    top: 5,
                    bottom: 50,
                    left: 0,
                    right: 0,
                },
            },
            onClick: onClocksClick,
            events: ["click", "contextmenu", "mousemove", "touchmove"],
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                title: {
                    display: true,
                    text: radioName,
                    font: { size: 18 },
                    padding: { bottom: 5 },
                },
                subtitle: {
                    display: true,
                    font: { size: 16 },
                    text: `${moment(data.date).hour(data.hour).format("L | LT")}`,
                    padding: { bottom: 50 },
                },
            },
            scales: {
                r: {
                    ticks: { display: false },
                    grid: { display: false },
                    angleLines: { display: false },
                    pointLabels: { display: false },
                    beginAtZero: true,
                    suggestedMax: ratingsData
                        ? Math.max(...ratingsData.data.flatMap(item => item.ratings.map(x => Math.max(x.connections, x.average)))) * 1.2
                        : 0,
                },
            },
            elements: {
                line: { borderWidth: 3 },
                point: { radius: 0 },
            },
        };
    }, [data.date, data.hour, onClocksClick, radioName, ratingsData]);

    const chartData = useMemo(() => {
        if (data) {
            const durations = [];
            const basisDatetime = moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm");
            const entries = data.broadcast_entries.sort((a, b) => new Date(a.played_at) - new Date(b.played_at));
            const labels = entries.map(x => {
                const duration = moment(x.duration * 1000).format("mm:ss");
                const title = `${x.artist} - ${x.title} - (${duration})`;
                return title.startsWith("#") ? title.replaceAll(" - ", "") : title;
            });
            entries.forEach((item, index) => {
                const nextItem = data.broadcast_entries[index + 1];
                // For first item, we compare next item to the beginning of the hour. If there is already no next item, it means we only have 1 item for the entire hour
                if (index === 0) {
                    const dur = nextItem ? moment(nextItem.played_at).diff(basisDatetime, "seconds") : 3600;
                    durations.push(dur);
                    return;
                }
                // For other items we keep filling until we have 3600 seconds, which means we cut last item
                const remaining = 3600 - durations.reduce((acc, val) => acc + val, 0);
                if (remaining) {
                    const dur = !nextItem
                        ? basisDatetime.add(1, "hours").diff(moment(item.played_at), "seconds")
                        : moment(nextItem.played_at).diff(moment(item.played_at), "seconds");
                    durations.push(dur > remaining ? remaining : dur);
                }
            });
            return {
                labels: labels,
                datasets: [
                    {
                        backgroundColor: data.broadcast_entries.map(x => mapItemTypeColor(x.item_type)),
                        data: durations,
                        datalabels: {
                            align: "end",
                            anchor: "end",
                            offset: 5,
                            borderRadius: 5,
                            clamp: true,
                            color: "#142533",
                            display: ctx => (ctx.active || ctx.hovered ? true : "auto"),
                            font: ctx => ({
                                size: 9,
                                weight: ctx.active || ctx.hovered ? "bold" : "normal",
                            }),
                            formatter: function (value, ctx) {
                                let result = ctx.chart.data.labels[ctx.dataIndex]?.split(" - ");
                                // If it is not just a gap, do extra formatting
                                if (result?.length > 1) {
                                    if (result[0].length > 15 && !ctx.active && !ctx.hovered) result[0] = result[0].substring(0, 12) + "...";
                                    if (result[1].length > 15 && !ctx.active && !ctx.hovered) result[1] = result[1].substring(0, 12) + "...";
                                }
                                return result;
                            },
                            backgroundColor: ctx => (ctx.active || ctx.hovered) && "#FEBE10",
                            listeners: {
                                enter: function (context, event) {
                                    context.hovered = true;
                                    return true;
                                },
                                leave: function (context, event) {
                                    context.hovered = false;
                                    return true;
                                },
                                click: function (context, event) {
                                    const item = data.broadcast_entries[context.dataIndex];
                                    setSelectedItem(item);
                                    if (item.item_type === "w") {
                                        setShowSongCatalogue(true);
                                    } else if (item.break_id) {
                                        setShowBreaksContent(true);
                                    }
                                },
                            },
                        },
                    },
                ],
            };
        }
    }, [data]);

    var ratingsChartData = useMemo(() => {
        if (ratingsData) {
            const labels = ratingsData.data[0].ratings.map(x => x);
            const datasets = ratingsData.data.map((x, i) => {
                return {
                    data: x.ratings.map(x => x.connections).concat([null]),
                    borderColor: clocksRatingsColors[i],
                    datalabels: {
                        align: "end",
                        anchor: "end",
                        color: "#142533",
                        borderColor: "white",
                        borderWidth: 1,
                        backgroundColor: clocksRatingsColors[i],
                        borderRadius: 5,
                        display: showRatingsLabels > 0 ? Array.from({ length: showRatingsLabels }, (_, j) => j === 0) : [false],
                        font: {
                            size: 12,
                            weight: "bold",
                        },
                    },
                };
            });
            datasets.push({
                data: ratingsData.data[0].ratings.map(x => x.average).concat([null]),
                borderColor: "#142533",
                datalabels: {
                    align: "start",
                    anchor: "start",
                    color: "#FEBE10",
                    borderColor: "white",
                    borderWidth: 1,
                    backgroundColor: "#142533",
                    borderRadius: 5,
                    display: showRatingsLabels > 0 ? Array.from({ length: showRatingsLabels }, (_, j) => j === 0) : [false],
                    font: {
                        size: 12,
                        weight: "bold",
                    },
                },
            });
            return {
                labels: labels,
                datasets: datasets,
            };
        }
    }, [ratingsData, showRatingsLabels]);

    function simulateRightClick(event) {
        if (event.touches.length === 1) {
            // Create a timeout to simulate right-click delay (optional)
            var timeoutId = setTimeout(function () {
                var simulatedEvent = new MouseEvent("contextmenu", {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    clientX: event.touches[0].clientX,
                    clientY: event.touches[0].clientY,
                });
                event.target.dispatchEvent(simulatedEvent);
            }, 500);

            // Clear timeout on touch end
            event.target.addEventListener("touchend", function () {
                clearTimeout(timeoutId);
            });
            event.target.addEventListener("touchmove", function () {
                clearTimeout(timeoutId);
            });
        }
    }

    function onBreakChange(breakId) {
        setSelectedItem(data.broadcast_entries.find(x => x.break_id === breakId));
    }

    function toggleMinuteByMinutePanel() {
        setShowMinuteByMinute(current => !current);
        setShowContextMenu(false);
    }

    function toggleTrendOverTimePanel() {
        setShowTrendOvertTime(current => !current);
        setShowContextMenu(false);
    }

    function toggleListenersPanel() {
        setShowListeners(current => !current);
        setShowContextMenu(false);
    }

    function toggleSongCataloguePanel() {
        setShowSongCatalogue(current => !current);
        setShowContextMenu(false);
    }

    function toggleSongHistoryPanel() {
        setShowSongHistory(current => !current);
        setShowContextMenu(false);
    }

    function toggleBreaksContentPanel() {
        setShowBreaksContent(current => !current);
        setShowContextMenu(false);
    }

    const contextMenuList = [
        {
            title: "Add bit to collection",
            func: () => {
                setShowAddBitModal(true);
                setNewBitItem({
                    placeholder: selectedItem.item_id > 0 ? selectedItem.artist + " - " + selectedItem.title : null,
                    bitStart: selectedItem.played_at,
                    bitEnd: moment(selectedItem.played_at).add(selectedItem.duration, "s").format("YYYY-MM-DDTHH:mm:ss"),
                });
            },
            disabled: !addToCollectionAllowed,
        },
        {
            title: "Add selection to collection",
            func: () => {
                setShowAddBitModal(true);
                setNewBitItem({
                    bitStart: moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").add(markInSeconds, "seconds").format("YYYY-MM-DDTHH:mm:ss"),
                    bitEnd: moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").add(markOutSeconds, "seconds").format("YYYY-MM-DDTHH:mm:ss"),
                });
            },
            disabled: markInSeconds === 0 || markOutSeconds === 0 || !addToCollectionAllowed,
        },
        { divider: true },
        {
            title: "Toggle audio playback",
            func: () => toggleAudioPlay(audioRef.current, setIsPlaying, isAudioAvailable),
            key: "Space",
            disabled: !isAudioAvailable,
        },
        {
            title: "Speed up audio",
            func: () => increasePlaybackSpeed(audioRef.current, setPlaybackRate, isAudioAvailable),
            key: "+",
            disabled: !isAudioAvailable,
        },
        {
            title: "Slow down audio",
            func: () => decreasePlaybackSpeed(audioRef.current, setPlaybackRate, isAudioAvailable),
            key: "-",
            disabled: !isAudioAvailable,
        },
        { divider: true },
        {
            title: `Mark in${markInSeconds > 0 ? ` (${formatTime(Math.round(markInSeconds))})` : ""}`,
            func: () => setMark(setMarkInSeconds, clickedTime),
            key: "I",
            disabled: clickedTime > markOutSeconds && markOutSeconds !== 0,
        },
        {
            title: `Mark out${markOutSeconds > 0 ? ` (${formatTime(Math.round(markOutSeconds))})` : ""}`,
            func: () => setMark(setMarkOutSeconds, clickedTime),
            key: "O",
            disabled: clickedTime < markInSeconds,
        },
        { title: "Clear marks", func: () => clearMarks(setMarkInSeconds, setMarkOutSeconds), key: "C", disabled: markInSeconds + markOutSeconds === 0 },
        { divider: true },
        { title: "Show Song History", func: toggleSongHistoryPanel, key: "CTRL I", disabled: !musicEnabled || selectedItem?.item_id <= 0 },
        { title: "Show Listeners", func: toggleListenersPanel, key: "CTRL L", disabled: !listenersEnabled },
        { title: "Show Minute by Minute", func: toggleMinuteByMinutePanel, key: "CTRL M" },
        { title: "Show Trend over Time", func: toggleTrendOverTimePanel, key: "CTRL T", disabled: !musicTestingEnabled || selectedItem?.item_type !== "w" },
        { title: "Show Song Catalogue", func: toggleSongCataloguePanel, key: "CTRL C", disabled: !musicEnabled || selectedItem?.item_type !== "w" },
        {
            title: "Show Break Content",
            func: toggleBreaksContentPanel,
            key: "CTRL B",
            disabled: !contentTestingEnabled || !contentData?.find(x => x.break_id === selectedItem?.break_id),
        },
        { divider: true },
        {
            title: "Download hour",
            func: () => downloadMp3(data.radio_id, moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").format("YYYY-MM-DDTHH:mm:ss")),
            disabled: !isAudioAvailable,
        },
        {
            title: "Download bit",
            func: () => downloadMp3(data.radio_id, selectedItem.played_at, null, selectedItem.duration),
            disabled: !selectedItem || !isAudioAvailable,
        },
        {
            title: `Download Selection${markInSeconds > 0 && markOutSeconds > 0 ? ` (${formatTime(Math.round(markOutSeconds - markInSeconds))})` : ""}`,
            func: () =>
                downloadMp3(
                    data.radio_id,
                    moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").add(markInSeconds, "seconds").format("YYYY-MM-DDTHH:mm:ss"),
                    moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").add(markOutSeconds, "seconds").format("YYYY-MM-DDTHH:mm:ss"),
                ),
            disabled: !isAudioAvailable || markInSeconds === 0 || markOutSeconds === 0,
        },
        {
            title: "Download Aircheck to go",
            func: () => downloadMp3(data.radio_id, moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").format("YYYY-MM-DDTHH:mm:ss"), null, null, 1),
            disabled: !isAudioAvailable || !data.broadcast_entries.some(entry => (entry.item_type === "n" || entry.item_type === "e") && entry.duration > 20), // We need to speed it up before enabling
        },
        {
            title: "Download Music Flow to go",
            func: () => downloadMp3(data.radio_id, moment(`${data.date} ${data.hour}:00`, "YYYY-MM-DD H:mm").format("YYYY-MM-DDTHH:mm:ss"), null, null, 2),
            disabled: !isAudioAvailable || !data.broadcast_entries.some(entry => entry.item_type === "w"),
        },
    ];

    useOnKeyEventPress(() => (selectedItem?.item_id > 0 ? toggleSongHistoryPanel() : null), "i", true, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(
        () => musicTestingEnabled && selectedItem?.item_type === "w" && toggleTrendOverTimePanel(),
        "t",
        true,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(() => (listenersEnabled ? toggleListenersPanel() : null), "l", true, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(
        () => (markOutSeconds === 0 || currentAudioTime < markOutSeconds ? setMark(setMarkInSeconds, currentAudioTime) : null),
        "i",
        false,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(
        () => (currentAudioTime > markInSeconds ? setMark(setMarkOutSeconds, currentAudioTime) : null),
        "o",
        false,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(() => toggleSongCataloguePanel(), "c", true, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(
        () => contentTestingEnabled && contentData?.find(x => x.break_id === selectedItem?.break_id) && toggleBreaksContentPanel(),
        "b",
        true,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(() => clearMarks(setMarkInSeconds, setMarkOutSeconds), "c", false, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(() => toggleAudioPlay(audioRef.current, setIsPlaying, isAudioAvailable), " ", false, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(() => (selectedItem?.item_type === "w" ? toggleMinuteByMinutePanel() : null), "m", true, false, `#clocks-container-${data.radio_id}`);
    useOnKeyEventPress(
        () => increasePlaybackSpeed(audioRef.current, setPlaybackRate, isAudioAvailable),
        "+",
        false,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(
        () => decreasePlaybackSpeed(audioRef.current, setPlaybackRate, isAudioAvailable),
        "-",
        false,
        false,
        `#clocks-container-${data.radio_id}`,
    );
    useOnKeyEventPress(() => setShowContextMenu(false), "Escape");

    if (error) {
        return <ErrorResponse errorCode={error} />;
    }

    return (
        <>
            <div
                id={`clocks-container-${data.radio_id}`}
                ref={chartContainerRef}
                className={`position-relative clocks-container ${secondary && "secondary"}`}
                tabIndex={1}>
                <CreateBitModal show={showAddBitModal} onClose={() => setShowAddBitModal(false)} radioId={data.radio_id} newBit={newBitItem} />
                <Pie
                    ref={chartRef}
                    data={chartData}
                    options={chartOptions}
                    onTouchStart={simulateRightClick}
                    onContextMenu={e => e.preventDefault()}
                    className=" position-absolute"
                />
                <ClockHand chart={chartRef} currentAudioTime={currentAudioTime} />
                <ClockMark chart={chartRef} seconds={markInSeconds} markType={"in"} />
                <ClockMark chart={chartRef} seconds={markOutSeconds} markType={"out"} />
                {ratingsData && <Radar data={ratingsChartData} options={chartOptions} className=" position-absolute pe-none" />}
                <ClocksContextMenu
                    show={showContextMenu}
                    position={contextMenuPosition}
                    container={chartContainerRef}
                    contextMenuList={contextMenuList}
                    setShow={setShowContextMenu}
                />
                {loading && (
                    <div className="bg-dark bg-opacity-75 position-absolute top-0 bottom-0 start-0 end-0 d-flex flex-grow-1">
                        <LoadingIndicator width={100} theme="dark" />
                    </div>
                )}
            </div>
            {selectedItem && (
                <>
                    {selectedItem.break_id && contentData && (
                        <ClocksBreaksContentModal
                            show={showBreaksContent && contentTestingEnabled}
                            onHide={() => {
                                setSelectedItem(null);
                                setShowBreaksContent(false);
                            }}
                            breakItem={selectedItem}
                            breakContentData={contentData}
                            radioId={data.radio_id}
                            setBreaksContentData={setContentData}
                            onBreakChange={onBreakChange}
                        />
                    )}
                    {selectedItem.item_type === "w" && (
                        <>
                            <SongHistoryModal
                                show={showSongHistory}
                                onHide={() => {
                                    setSelectedItem(null);
                                    setShowSongHistory(false);
                                }}
                                song={selectedItem}
                                radioId={data?.radio_id}
                            />
                            <SongCatalogueModal
                                show={showSongCatalogue}
                                onHide={() => {
                                    setSelectedItem(null);
                                    setShowSongCatalogue(false);
                                }}
                                song={selectedItem}
                            />
                            <TrendOverTime
                                show={showTrendOvertTime}
                                onHide={() => setShowTrendOvertTime(false)}
                                radioId={selectedItem?.radio_id}
                                itemId={selectedItem?.item_id}
                                dataType={dataType}
                                daypartId={1}
                                monthsLimit={8}
                            />
                        </>
                    )}
                </>
            )}
            <Collapse in={showMinuteByMinute} mountOnEnter unmountOnExit onEntered={() => minuteByMinuteRef.current?.scrollIntoView({ behavior: "smooth" })}>
                <div ref={minuteByMinuteRef}>
                    <Stack direction="horizontal" className=" mb-1">
                        <h4 className="mb-0">Minute By Minute</h4>
                        <Button variant="ra-yellow" size="sm" className="ms-auto d-flex align-items-center" onClick={() => setShowMinuteByMinute(false)}>
                            Close <XLg />
                        </Button>
                    </Stack>
                    <MinuteByMinuteTable data={data?.broadcast_entries} />
                </div>
            </Collapse>
            <ListenersModal
                show={showListeners}
                onHide={() => setShowListeners(false)}
                radioId={data.radio_id}
                startDatetime={moment(`${data.date} ${data.hour}:00:00`, "YYYY-MM-DD H:mm:ss").format("YYYY-MM-DDTHH:mm:ss")}
                endDatetime={moment(`${data.date} ${data.hour}:59:59`, "YYYY-MM-DD H:mm:ss").format("YYYY-MM-DDTHH:mm:ss")}
            />
        </>
    );
}
